// components/FournisseurCard.js
import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import Icon from '../../../../components/icon/Icon';

const FournisseurCard = ({ fournisseur }) => {
  if (!fournisseur) {
    return (
      <Card className="border border-light shadow-sm bg-white mb-4">
        <Card.Body className="py-4 text-center">
          <div className="text-muted">
            <Icon icon="PersonOff" size="lg" className="mb-3 opacity-50" />
            <div className="small">Aucun fournisseur associé</div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // Helper functions
  const getInitials = (name) => name ? name.substring(0, 2).toUpperCase() : '??';
  
  // Format phone display
  const formatPhoneDisplay = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 8) {
      return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4');
    }
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
    }
    return phone;
  };

  // Format address for single line display
  const formatAddressSingleLine = (address) => {
    if (!address) return null;
    
    const parts = [];
    if (fournisseur.street) parts.push(fournisseur.street);
    if (fournisseur.city || fournisseur.zip) {
      const cityPart = fournisseur.zip ? `${fournisseur.zip} ${fournisseur.city || ''}` : fournisseur.city;
      if (cityPart) parts.push(cityPart);
    }
    return parts.join(', ');
  };

  const addressLine = formatAddressSingleLine();

  return (
    <Card className="border border-light shadow-sm bg-white mb-4">
      <Card.Header className="bg-white border-bottom-0 pt-3 px-4">
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center">
            <div className="bg-light text-dark rounded-circle d-flex align-items-center justify-content-center fw-bold border me-3" 
                 style={{ width: 40, height: 40, fontSize: '0.85rem' }}>
              {getInitials(fournisseur.nom)}
            </div>
            <div>
              <h5 className="fw-bold text-dark mb-1">{fournisseur.nom}</h5>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Badge bg="light" text="dark" className="fw-normal border small">
                  Fournisseur
                </Badge>
                {fournisseur.RefFournisseur && (
                  <Badge bg="info" className="fw-normal small">
                    {fournisseur.RefFournisseur}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card.Header>
      
      <Card.Body className="px-4 pt-0">
        {/* LINE 1: Contact Information */}
        <div className="py-2 border-bottom">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex align-items-center">
              <Icon icon="Phone" size="xs" className="me-2 text-muted" />
              <span className="text-muted small me-2">Tél:</span>
              <span className="fw-medium text-dark">
                {fournisseur.telephone ? formatPhoneDisplay(fournisseur.telephone) : '-'}
              </span>
            </div>
            
            <div className="d-flex align-items-center">
              <Icon icon="Email" size="xs" className="me-2 text-muted" />
              <span className="text-muted small me-2">Email:</span>
              <span className="fw-medium text-dark">
                {fournisseur.email || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* LINE 2: Address */}
        {addressLine && (
          <div className="py-2 border-bottom">
            <div className="d-flex align-items-start">
              <Icon icon="LocationOn" size="xs" className="me-2 text-muted mt-1" />
              <div>
                <span className="text-muted small d-block mb-1">Adresse:</span>
                <span className="fw-medium text-dark">
                  {addressLine}
                  {fournisseur.pays && fournisseur.pays !== 'France' && (
                    <span className="text-muted ms-2">({fournisseur.pays})</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* LINE 3: Additional Info */}
        {(fournisseur.state || fournisseur.city || fournisseur.zip) && (
          <div className="py-2 border-bottom">
            <div className="d-flex flex-wrap align-items-center gap-3">
              {fournisseur.state && (
                <div className="d-flex align-items-center">
                  <Icon icon="Map" size="xs" className="me-2 text-muted" />
                  <span className="text-muted small me-2">Région:</span>
                  <span className="fw-medium text-dark">{fournisseur.state}</span>
                </div>
              )}
              
              {fournisseur.city && (
                <div className="d-flex align-items-center">
                  <Icon icon="LocationCity" size="xs" className="me-2 text-muted" />
                  <span className="text-muted small me-2">Ville:</span>
                  <span className="fw-medium text-dark">{fournisseur.city}</span>
                </div>
              )}
              
              {fournisseur.zip && (
                <div className="d-flex align-items-center">
                  <Icon icon="LocalPostOffice" size="xs" className="me-2 text-muted" />
                  <span className="text-muted small me-2">Code Postal:</span>
                  <span className="fw-medium text-dark">{fournisseur.zip}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* LINE 4: Registration Info */}
        <div className="pt-2">
          <div className="d-flex flex-wrap align-items-center gap-3">
            {fournisseur.createdAt && (
              <div className="d-flex align-items-center">
                <Icon icon="CalendarToday" size="xs" className="me-2 text-muted" />
                <span className="text-muted small me-2">Inscrit:</span>
                <span className="fw-medium text-dark">
                  {new Date(fournisseur.createdAt).toLocaleDateString('fr-FR')}
                </span>
              </div>
            )}
            
            {fournisseur.updatedAt && fournisseur.updatedAt !== fournisseur.createdAt && (
              <div className="d-flex align-items-center">
                <Icon icon="Update" size="xs" className="me-2 text-muted" />
                <span className="text-muted small me-2">Mise à jour:</span>
                <span className="fw-medium text-dark">
                  {new Date(fournisseur.updatedAt).toLocaleDateString('fr-FR')}
                </span>
              </div>
            )}
            
            {fournisseur.produits && fournisseur.produits.length > 0 && (
              <div className="d-flex align-items-center">
                <Icon icon="Inventory" size="xs" className="me-2 text-muted" />
                <span className="text-muted small me-2">Produits:</span>
                <span className="fw-medium text-dark">{fournisseur.produits.length}</span>
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default FournisseurCard;